import { ChannelMessageCollection, IssueCaseDoc, IssueCaseMessageLink } from '@gooduncles/gu-app-schema';

import { ChannelMessageWithCollection } from 'src/utils/channel/channel-message-util';
import { formatDate } from 'src/utils/date-util';
import { deleteIssueCaseMessageLink, setIssueCaseMessageLink } from 'src/utils/firebase/firebase-short-cut';

/** 매장 채팅 채널 ID prefix — ChannelMessage.channelId = `${STORE_CHANNEL_PREFIX}${storeId}` */
export const STORE_CHANNEL_PREFIX = 'store_';

export const getStoreChannelId = (storeId: string) => `${STORE_CHANNEL_PREFIX}${storeId}`;

export const makeIssueCaseMessageLinkId = (issueCaseId: string, messageId: string) => `${issueCaseId}__${messageId}`;

export interface SelectedMessageRef {
  messageId: string;
  messageCollection: ChannelMessageCollection;
  messageCreatedAt: string;
}

/**
 * 이미 태깅된 doc → SelectedMessageRef. 호출자가 inline 으로 같은 변환을 반복하지 않도록 일원화한다.
 * (annotated 가 아닌 raw 메시지에서는 만들 수 없게 막아 컬렉션 재추론 가능성을 차단)
 */
export const toSelectedMessageRef = (doc: ChannelMessageWithCollection): SelectedMessageRef => ({
  messageId: doc._id,
  messageCollection: doc.messageCollection,
  messageCreatedAt: doc.createdAt,
});

interface LinkPayloadInput {
  issueCase: Pick<IssueCaseDoc, '_id' | 'storeId' | 'status'>;
  message: SelectedMessageRef;
  createdBy: string;
  note?: string | null;
}

export const buildManualLinkPayload = ({
  issueCase,
  message,
  createdBy,
  note,
}: LinkPayloadInput): IssueCaseMessageLink => ({
  issueCaseId: issueCase._id,
  messageId: message.messageId,
  messageCollection: message.messageCollection,
  linkedBy: 'manual',
  status: 'confirmed',
  createdBy,
  createdAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
  reviewedBy: null,
  reviewedAt: null,
  ai: null,
  messageCreatedAt: message.messageCreatedAt,
  storeId: issueCase.storeId,
  caseStatus: issueCase.status,
  note: note?.trim() ? note.trim() : null,
});

export interface LinkMessagesResult {
  successCount: number;
  failureCount: number;
  failedMessageIds: string[];
}

/**
 * 선택된 메시지들을 결정론적 ID 로 upsert. 같은 (case, message) 쌍이 다시 들어와도 멱등.
 * write 끼리 독립이라 병렬 실행. 부분 성공 허용 — 호출 측에서 successCount/failureCount 로 토스트 분기.
 */
export const linkMessagesToIssueCase = async (
  issueCase: Pick<IssueCaseDoc, '_id' | 'storeId' | 'status'>,
  messages: SelectedMessageRef[],
  createdBy: string,
  note?: string | null,
): Promise<LinkMessagesResult> => {
  const results = await Promise.allSettled(
    messages.map(async (message) => {
      const linkId = makeIssueCaseMessageLinkId(issueCase._id, message.messageId);
      const payload = buildManualLinkPayload({ issueCase, message, createdBy, note });
      await setIssueCaseMessageLink(linkId, payload);
    }),
  );

  const failedMessageIds = results
    .map((result, index) => {
      if (result.status === 'rejected') {
        console.error('[linkMessagesToIssueCase] failed', messages[index].messageId, result.reason);
        return messages[index].messageId;
      }
      return null;
    })
    .filter((id): id is string => id !== null);

  return {
    successCount: messages.length - failedMessageIds.length,
    failureCount: failedMessageIds.length,
    failedMessageIds,
  };
};

export const unlinkMessageFromIssueCase = (linkId: string) => deleteIssueCaseMessageLink(linkId);

/** UI 표시용 본문 미리보기 (linkedMessageStats.snippet 와 동일 컨벤션, 200자) */
export const getMessageBodyPreview = (body: string | null, maxLength = 200): string => {
  if (!body) return '';
  const trimmed = body.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength)}…`;
};
